import { SettingsRow } from "@rosc/ui/v32/settings-row";
import { Pencil } from "lucide-react";
import type { JSX } from "react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface Props {
  label: string;
  value: string;
  onClick?: () => void;
}

// v3.2: one settings row (@rosc/ui SettingsRow), the whole row opens the
// field's edit sheet (iOS Settings). Takes the dialog trigger's props
// (aria-expanded, data-state).
const V3FieldRow = forwardRef<
  HTMLButtonElement,
  Props & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, value, ...props }, ref) => {
  const { t } = useTranslation();
  return (
    <SettingsRow
      ref={ref}
      aria-label={t("settings.profile.editField", { field: label })}
      label={label}
      placeholder={t("settings.profile.notSet")}
      value={value}
      {...props}
    />
  );
});
V3FieldRow.displayName = "V3FieldRow";

export const ReadOnlyInputEdit = forwardRef<
  HTMLButtonElement,
  Props & React.ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref): JSX.Element => {
  if (AppConfig.enableAppRedesign) return <V3FieldRow ref={ref} {...props} />;
  return (
    <div className="flex items-center justify-between">
      <div className="space-y-1">
        <Label className="text-md">{props.label}</Label>
        <p>{props.value}</p>
      </div>
      <Button size="icon" type="button" variant="ghost" onClick={props.onClick}>
        <Pencil className="h-4 w-4" />
      </Button>
    </div>
  );
});
ReadOnlyInputEdit.displayName = "ReadOnlyInputEdit";
